/**
 * Monitoring Types - Main Export
 *
 * Central export point for all monitoring types, interfaces, and enums.
 * This provides a clean import interface for the rest of the application.
 */

// Export all enums
export {
  ActionEventType,
  ActionStatus,
  ActionType,
  DeviceType,
  EVENT_CATEGORY_MAP,
  EventCategory,
  EventEventType,
  LogLevel,
  SamplingStrategy,
  WorkflowType,
} from './enums';

// Export all interfaces
export type {
  ActionContext,
  ActionTrackingConfig,
  BusinessContext,
  ContextEnricher,
  DatadogIntegration,
  EnhancedTrackEventParams,
  ErrorContext,
  EventBatch,
  PerformanceMetrics,
  TechnicalContext,
  UserAction,
  UserContext,
  WorkflowTracking,
} from './interfaces';

// Export focused context data interfaces
export type {
  MonitoringContextData,
  MonitoringContextProvider,
  OrganizationContextData,
  UserContextData,
  VideoContextData,
} from './contextData';

// Export configuration types
export type {
  BatchingConfig,
  EnvironmentConfig,
  MonitoringFeatureFlags,
  PerformanceConfig,
  SamplingConfig,
} from './config';

// Export configuration defaults and utilities
export {
  ConfigValidator,
  DEFAULT_BATCHING_CONFIG,
  DEFAULT_FEATURE_FLAGS,
  DEFAULT_MONITORING_CONFIG,
  DEFAULT_PERFORMANCE_CONFIG,
  DEFAULT_SAMPLING_CONFIG,
} from './config';

// Export services and utilities
export { MonitoringService } from '../services';

/**
 * Monitoring constants
 */
export const MONITORING_CONSTANTS = {
  /** Maximum action duration before considering it stuck (30 minutes) */
  MAX_ACTION_DURATION: 30 * 60 * 1000,
  /** Maximum queue size before dropping events */
  MAX_QUEUE_SIZE: 1000,
  /** Default transaction ID prefix */
  TRANSACTION_ID_PREFIX: 'txn_',
  /** Default workflow ID prefix */
  WORKFLOW_ID_PREFIX: 'wf_',
  /** Monitoring version for compatibility tracking */
  MONITORING_VERSION: '1.0.0',
} as const;
